import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';
import './SLAManager.css';

// Tightest first, which is the order somebody reads a ladder in.
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const SLAManager = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [slaRules, setSLARules] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const escalationOptions = [
    { value: 'escalate_priority', label: 'Auto-escalate Priority' },
    { value: 'email_manager', label: 'Email Manager' },
    { value: 'email_requester', label: 'Email Requester' },
    { value: 'auto_assign', label: 'Auto-assign to Senior Tech' },
    { value: 'add_comment', label: 'Add Comment to Ticket' }
  ];

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  /**
   * The tabs used to be the hard-coded list
   * ['general', 'technical', 'billing', 'compliance'], which is what
   * sla_rules ships four rows for - and the categories a ticket can actually
   * be filed under are Hardware, Software, Network, Account and Other, from
   * the categories table.
   *
   * Those two lists have never had a name in common. The SLA tracker looks a
   * rule up by the ticket's category name, finds nothing, and falls back to a
   * default keyed off priority - so every figure typed into this page was
   * configuring a rule that no ticket in the system could ever match.
   *
   * Migration 017 removes those four rows and gives every real category a rule
   * of its own, so after it has run these tabs are the categories and nothing
   * else. The orphan handling below stays, because it is not only about those
   * four: rename a category with a rule already against it and the rule is
   * orphaned the same way, and the page should say so rather than let the
   * tracker quietly fall back to priority defaults again.
   *
   * It also has to work BEFORE 017 runs. This app is deployed by pushing and
   * migrated by hand afterwards, so there is always a window where the new
   * code meets the old data - here that window shows the four old rows as
   * marked tabs, which is exactly what it should show.
   */
  const load = useCallback(async () => {
    try {
      const [rulesRes, catsRes] = await Promise.all([
        axios.get(`${API_BASE}/sla-rules`),
        axios.get(`${API_BASE}/categories`).catch(() => ({ data: [] }))
      ]);
      // Guard the shape: .find/.map on a wrapped object throws during render
      // and takes the whole Settings page down with it.
      const data = rulesRes.data;
      setSLARules(Array.isArray(data) ? data : (data?.sla_rules ?? []));
      const cats = Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data?.categories ?? []);
      setTicketCategories(cats.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean));
      setError(null);
    } catch (err) {
      console.error('Error fetching SLA rules:', err);
      setError('Failed to load SLA rules');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    load();
  }, [load]);

  const same = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

  // Every real category, plus any stored rule whose name is not one of them.
  // Memoised because it is a useEffect dependency: rebuilt on every render it
  // would re-run the effect on every render, and CI=true makes that warning a
  // build failure rather than a note.
  const categories = useMemo(() => {
    const orphaned = slaRules
      .map((r) => r.category)
      .filter((c) => !ticketCategories.some((t) => same(t, c)));
    return [...ticketCategories, ...orphaned];
  }, [slaRules, ticketCategories]);

  useEffect(() => {
    if (!activeCategory && categories.length > 0) setActiveCategory(categories[0]);
  }, [activeCategory, categories]);

  const getCurrentRule = () =>
    slaRules.find((r) => same(r.category, activeCategory));

  // A rule this page can show but no ticket can ever match.
  const isOrphan = (cat) => !ticketCategories.some((t) => same(t, cat));

  const handleSaveRule = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const escalationActions = escalationOptions
      .filter(opt => formData.get(`escalation_${opt.value}`))
      .map(opt => opt.value);

    // Per-priority targets. These are what actually govern a ticket - the
    // response/resolution pair above them is the fallback for a priority with
    // no figure of its own. Before this form could set them, the column was
    // written by nothing and every priority in a category shared one target,
    // so a Critical outage and a Low cosmetic fault had the same clock.
    const priorityOverrides = {};
    for (const name of PRIORITIES) {
      const resp = parseInt(formData.get(`pr_${name}_response`), 10);
      const reso = parseInt(formData.get(`pr_${name}_resolution`), 10);
      const entry = {};
      if (Number.isFinite(resp) && resp > 0) entry.response_time_hours = resp;
      if (Number.isFinite(reso) && reso > 0) entry.resolution_time_hours = reso;
      // An empty pair means "no special target for this priority", which is a
      // real choice and must not be stored as a zero.
      if (Object.keys(entry).length > 0) priorityOverrides[name] = entry;
    }

    const ruleData = {
      category: activeCategory,
      response_time_hours: parseInt(formData.get('response_time_hours')),
      resolution_time_hours: parseInt(formData.get('resolution_time_hours')),
      grace_period_hours: parseInt(formData.get('grace_period_hours')) || 0,
      enabled: formData.get('enabled') === 'on',
      escalation_actions: escalationActions,
      priority_overrides: priorityOverrides
    };

    try {
      const currentRule = getCurrentRule();

      let response;
      if (currentRule) {
        response = await axios.put(
          `${API_BASE}/sla-rules/${currentRule.id}`,
          ruleData
        );
      } else {
        response = await axios.post(
          `${API_BASE}/sla-rules`,
          ruleData
        );
      }

      // Update local state.
      //
      // The sort used to be a lookup table of the four names this table
      // shipped with - general, technical, billing, compliance - which
      // returned undefined for every real category, so the comparator answered
      // NaN and the order after a save was whatever the engine felt like.
      // The tabs are the order; the list follows them.
      setSLARules(prev => {
        const filtered = prev.filter(r => !same(r.category, activeCategory));
        const rank = (r) => {
          const i = categories.findIndex((c) => same(c, r.category));
          return i === -1 ? categories.length : i;
        };
        return [...filtered, response.data].sort((a, b) => rank(a) - rank(b));
      });

      setSuccess(`✅ SLA rule for ${activeCategory} saved successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving SLA rule:', err);
      setError('Failed to save SLA rule');
    }
  };

  const handleDeleteRule = async () => {
    const currentRule = getCurrentRule();
    if (!currentRule) return;

    if (!(await confirm({ title: `Delete SLA rule for ${activeCategory}?`, confirmLabel: 'Delete', danger: true }))) return;

    try {
      await axios.delete(
        `${API_BASE}/sla-rules/${currentRule.id}`
      );

      setSLARules(prev => prev.filter(r => !same(r.category, activeCategory)));
      setSuccess(`✅ SLA rule for ${activeCategory} deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting SLA rule:', err);
      setError('Failed to delete SLA rule');
    }
  };

  if (loading) {
    return <div className="sla-manager loading">Loading SLA configuration...</div>;
  }

  const currentRule = getCurrentRule();

  return (
    <div className="sla-manager">
      {/* The page's own title is Settings' (SLA rules). */}

      {error && <div className="sla-error">⚠️ {error}</div>}
      {success && <div className="sla-success">{success}</div>}

      {/* Category Tabs */}
      <div className="sla-tabs">
        {categories.map(cat => (
          <button
            key={cat}
            className={`sla-tab ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
            {/* The tick used to be driven by currentRule, which is the rule
                for the ACTIVE tab - so every tab showed a tick as soon as any
                one of them had a rule. It is per-tab now. */}
            {slaRules.some((r) => same(r.category, cat)) && (
              <span className="sla-tab-badge">✓</span>
            )}
            {isOrphan(cat) && <span className="sla-tab-warn" title="Matches no ticket category">!</span>}
          </button>
        ))}
      </div>

      {/* A category with no row in sla_rules used to render the form's fallback
          values - 4 / 24 / 0 - identically for every category, which read as
          "every category has the same SLA" when the truth was "none of them has
          one". Say which it is. */}
      {!currentRule && (
        <p className="sla-unset" role="status">
          No SLA rule is configured for <strong>{activeCategory}</strong>. The
          figures below are defaults, not settings &mdash; save to create the rule.
        </p>
      )}

      {/* A rule named for something no ticket can be filed under never applies
          to a single ticket. Said here rather than left for somebody to
          discover from the tracker quietly showing default figures. */}
      {activeCategory && isOrphan(activeCategory) && (
        <p className="sla-orphan" role="alert">
          No ticket can be filed under <strong>{activeCategory}</strong>, so
          this rule never applies to anything. Tickets are categorised as{' '}
          {ticketCategories.join(', ') || '(no categories are configured)'}
          {' '}&mdash; either rename this rule to one of those, or add{' '}
          <strong>{activeCategory}</strong> as a category under Categories above.
          Until then these figures are stored and ignored, and the SLA tracker
          falls back to a default based on the ticket&rsquo;s priority.
        </p>
      )}

      {/* SLA Configuration Form */}
      <form onSubmit={handleSaveRule} className="sla-form">
        <div className="sla-section">
          <h4>Response & Resolution Times</h4>

          <div className="sla-row">
            <div className="sla-field">
              <label htmlFor="response_time">Response Time (Hours)</label>
              <input
                id="response_time"
                type="number"
                name="response_time_hours"
                min="1"
                defaultValue={currentRule?.response_time_hours || 4}
                required
              />
              <small>Time to provide first response</small>
            </div>

            <div className="sla-field">
              <label htmlFor="resolution_time">Resolution Time (Hours)</label>
              <input
                id="resolution_time"
                type="number"
                name="resolution_time_hours"
                min="1"
                defaultValue={currentRule?.resolution_time_hours || 24}
                required
              />
              <small>Time to fully resolve ticket</small>
            </div>

            <div className="sla-field">
              <label htmlFor="grace_period">Grace Period (Hours)</label>
              <input
                id="grace_period"
                type="number"
                name="grace_period_hours"
                min="0"
                defaultValue={currentRule?.grace_period_hours || 0}
              />
              <small>Warn before breach by this amount</small>
            </div>
          </div>
        </div>

        {/* What this category promises per priority. The pair above is the
            fallback for anything left blank here. */}
        <div className="sla-section">
          <h4>Targets by priority</h4>
          <p className="sla-note">
            What this category promises for each priority. Leave a pair blank to
            fall back to the figures above.
          </p>
          <div className="table-scroll">
          <table className="sla-ladder">
            <thead>
              <tr>
                <th scope="col">Priority</th>
                <th scope="col">Response (hours)</th>
                <th scope="col">Resolution (hours)</th>
              </tr>
            </thead>
            <tbody>
              {PRIORITIES.map((name) => {
                const o = (currentRule?.priority_overrides || {})[name] || {};
                return (
                  <tr key={name}>
                    <th scope="row">{name}</th>
                    <td>
                      <input
                        type="number"
                        min="1"
                        name={`pr_${name}_response`}
                        defaultValue={o.response_time_hours ?? ''}
                        aria-label={`${name} response time in hours`}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        name={`pr_${name}_resolution`}
                        defaultValue={o.resolution_time_hours ?? ''}
                        aria-label={`${name} resolution time in hours`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Escalation Actions */}
        <div className="sla-section">
          <h4>Escalation Actions (on breach)</h4>
          <div className="sla-escalation-grid">
            {escalationOptions.map(option => (
              <label key={option.value} className="sla-checkbox-label">
                <input
                  type="checkbox"
                  name={`escalation_${option.value}`}
                  defaultChecked={currentRule?.escalation_actions?.includes(option.value) || false}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {/* Enable/Disable & Actions */}
        <div className="sla-section">
          <div className="sla-control">
            <label className="sla-checkbox-label">
              <input
                type="checkbox"
                name="enabled"
                defaultChecked={currentRule?.enabled !== false}
              />
              Enable SLA Tracking for {activeCategory}
            </label>
          </div>

          <div className="sla-actions">
            <button type="submit" className="btn-save">
              💾 Save SLA Configuration
            </button>
            {currentRule && (
              <button
                type="button"
                className="btn-delete"
                onClick={handleDeleteRule}
              >
                🗑️ Delete Rule
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Info Panel */}
      <div className="sla-info">
        <h4>Current Configuration</h4>
        {currentRule ? (
          <div className="sla-info-content">
            <p><strong>Category:</strong> {activeCategory}</p>
            <p><strong>Response Time:</strong> {currentRule.response_time_hours} hours</p>
            <p><strong>Resolution Time:</strong> {currentRule.resolution_time_hours} hours</p>
            <p><strong>Grace Period:</strong> {currentRule.grace_period_hours} hours</p>
            <p><strong>Status:</strong> {currentRule.enabled ? '🟢 Enabled' : '🔴 Disabled'}</p>
            <p><strong>Escalation Actions:</strong></p>
            <ul>
              {currentRule.escalation_actions?.length > 0 ? (
                currentRule.escalation_actions.map(action => (
                  <li key={action}>{escalationOptions.find(opt => opt.value === action)?.label}</li>
                ))
              ) : (
                <li><em>None configured</em></li>
              )}
            </ul>
          </div>
        ) : (
          <div className="sla-info-content">
            <p>No SLA rule configured for {activeCategory}. Save a configuration above to get started.</p>
          </div>
        )}
      </div>
      {confirmDialog}
    </div>
  );
};

export default SLAManager;
